import React, { useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, Button, Row, Col } from 'react-bootstrap';
import BarChartIcon from '@mui/icons-material/BarChart';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';

import { isSignedIn } from '../utils/localStorage';

const PublicLayout: React.FC = () => {
  const location = useLocation();

  // These dashboard pages are reachable at a public address as well as inside
  // the application, and this header used to offer a Sign In button and no way
  // back regardless of who was looking at it. A signed in person following the
  // Total Returns Dashboard link therefore lost the navigation and every route
  // into their scripts, which reads as having been logged out even though the
  // session was still there. Checked per render rather than held in state, so
  // signing out in another tab is reflected here.
  const signedIn = useMemo(() => isSignedIn(), [location.pathname]);

  return (
    <div className="public-layout">
      <Navbar bg="dark" variant="dark" expand="lg" className="shadow-sm">
        <Container fluid>
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
            <BarChartIcon style={{ fontSize: 24 }} className="me-2" />
            <span className="fw-bold">Total Returns</span>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="public-navbar" />

          <Navbar.Collapse id="public-navbar">
            <Nav className="me-auto">
              <Nav.Link
                as={Link}
                to="/"
                active={location.pathname === '/'}
              >
                <HomeIcon style={{ fontSize: 18 }} className="me-1" />
                Home
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/dashboard"
                active={location.pathname.startsWith('/dashboard')}
              >
                <TrendingUpIcon style={{ fontSize: 18 }} className="me-1" />
                Total Returns Dashboard
              </Nav.Link>
            </Nav>

            <Nav>
              {signedIn ? (
                <Link to="/app/allscripts" className="text-decoration-none">
                  <Button variant="outline-light" size="sm" className="ms-2">
                    <DescriptionIcon
                      style={{ fontSize: 18 }}
                      className="me-1"
                    />
                    Back to my scripts
                  </Button>
                </Link>
              ) : (
                <Link to="/login" className="text-decoration-none">
                  <Button variant="outline-light" size="sm" className="ms-2">
                    <LoginIcon style={{ fontSize: 18 }} className="me-1" />
                    Sign In
                  </Button>
                </Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="public-content">
        <Outlet />
      </div>

      <footer className="bg-dark text-light py-4 mt-5">
        <Container>
          <Row className="g-4">
            {/* About Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                About Total Returns
              </h6>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: 0 }}>
                Institutional-grade investment research and market analytics platform,
                accessible to anyone who wants to learn.
              </p>
            </Col>

            {/* Contact Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                Contact Us
              </h6>
              <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                <div className="d-flex align-items-center mb-2">
                  <EmailIcon style={{ fontSize: 16, marginRight: '8px' }} />
                  <a
                    href="mailto:support@totalreturns.net"
                    style={{ color: '#9ca3af', textDecoration: 'none' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                  >
                    support@totalreturns.net
                  </a>
                </div>
                <p style={{ marginBottom: 0 }}>
                  For inquiries, feedback, or support requests
                </p>
              </div>
            </Col>

            {/* Legal Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                Legal
              </h6>
              <div style={{ fontSize: '13px' }}>
                <Link
                  to="/terms"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Disclaimer &amp; Terms of Use
                </Link>
                <Link
                  to="/privacy"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/about"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Contact Us
                </Link>
              </div>
            </Col>
          </Row>

          {/* Bottom Bar */}
          <div
            className="text-center pt-4 mt-4"
            style={{ borderTop: '1px solid #374151' }}
          >
            <small style={{ color: '#9ca3af', fontSize: '12px' }}>
              &copy; {new Date().getFullYear()} Total Returns. All rights reserved.
              Data provided for informational purposes only and should not be considered investment advice.
            </small>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default PublicLayout;
